import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { API } from '../config/api';

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const shine = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const PortfolioContainer = styled(motion.div)`
  min-height: 100vh;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  padding: 6rem 2rem 4rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
                radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.3) 0%, transparent 50%);
    animation: ${float} 20s ease-in-out infinite;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const Header = styled(motion.div)`
  text-align: center;
  margin-bottom: 4rem;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 6vw, 4rem);
  color: #ffffff;
  font-weight: 900;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 300;
  letter-spacing: 1px;
`;

const FilterTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
`;

const FilterTab = styled(motion.button)`
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 0.8rem 1.5rem;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const BooksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 3rem;
  margin-bottom: 4rem;
`;

const BookCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  overflow: hidden;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-10px) rotateY(5deg);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }
`;

const BookImageWrapper = styled.div`
  position: relative;
  height: 300px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    animation: ${shine} 3s infinite;
  }
`;

const BookImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  transition: transform 0.3s ease;
  border-radius: 8px;
  
  ${BookCard}:hover & {
    transform: scale(1.05);
  }
`;

const BookContent = styled.div`
  padding: 2rem;
  color: white;
`;

const BookTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #fff;
`;

const BookGenre = styled.span`
  display: inline-block;
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
  margin-bottom: 1rem;
`;

const BookDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 1.5rem;
`;

const BookStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const Stat = styled.div`
  text-align: center;
  
  .value {
    font-size: 1.2rem;
    font-weight: 700;
    color: #4facfe;
  }
  
  .label {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.7);
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`;

const ReadMoreButton = styled(motion.button)`
  background: linear-gradient(45deg, #4facfe, #00f2fe);
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(79, 172, 254, 0.4);
  }
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled(motion.div)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  padding: 3rem;
  max-width: 600px;
  width: 100%;
  color: white;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: rotate(90deg);
  }
`;

const AchievementsSection = styled(motion.div)`
  text-align: center;
  margin-top: 4rem;
  padding: 3rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const AchievementsTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 2rem;
  color: #4facfe;
`;

const AchievementsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const Achievement = styled(motion.div)`
  text-align: center;
  color: white;
  
  .icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }
  
  .number {
    font-size: 2.5rem;
    font-weight: 700;
    color: #f093fb;
    margin-bottom: 0.5rem;
  }
  
  .text {
    font-size: 1rem;
    opacity: 0.9;
  }
`;

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedBook, setSelectedBook] = useState(null);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genres, setGenres] = useState(['All']);
  
  useEffect(() => {
    fetchBooks();
  }, []);
  
  const fetchBooks = async () => {
    try {
      const response = await fetch(`${API}/books`);
      if (response.ok) {
        const booksData = await response.json();
        // Only show published books in public portfolio
        const publishedBooks = booksData.filter(book => book.published);
        setBooks(publishedBooks);
        
        // Extract unique genres
        const uniqueGenres = ['All', ...new Set(publishedBooks.map(book => book.genre).filter(Boolean))];
        setGenres(uniqueGenres);
      }
    } catch (error) {
      console.error('Error fetching books:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate average rating from books with ratings
  const booksWithRatings = books.filter(book => book.rating && book.rating !== '');
  const averageRating = booksWithRatings.length > 0 
    ? (booksWithRatings.reduce((sum, book) => sum + parseFloat(book.rating), 0) / booksWithRatings.length).toFixed(1)
    : '4.8';

  const achievements = [
    { icon: '📚', number: books.length.toString(), text: 'Published Books' },
    { icon: '⭐', number: averageRating, text: 'Average Rating' },
    { icon: '📖', number: '500K+', text: 'Pages Read' }
  ];
  
  const filteredBooks = activeFilter === 'All' 
    ? books 
    : books.filter(book => book.genre === activeFilter);
  
  return (
    <PortfolioContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      <ContentWrapper>
        <Header
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Title>Literary Portfolio</Title>
          <Subtitle>A Collection of Stories That Define Me</Subtitle>
        </Header>
        
        <FilterTabs>
          {genres.map((filter, index) => (
            <FilterTab
              key={filter}
              active={activeFilter === filter}
              onClick={() => setActiveFilter(filter)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {filter}
            </FilterTab>
          ))}
        </FilterTabs>
        
        {loading ? (
          <div style={{ textAlign: 'center', color: 'white', fontSize: '1.2rem', padding: '4rem' }}>
            Loading your literary collection...
          </div>
        ) : (
          <BooksGrid>
            <AnimatePresence>
              {filteredBooks.length > 0 ? filteredBooks.map((book, index) => (
                <BookCard
                  key={book.id}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -50 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setSelectedBook(book)}
                >
                  <BookImageWrapper>
                    <BookImage 
                      src={book.coverImage ? `${API}${book.coverImage}` : 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400&h=600&fit=crop'} 
                      alt={book.title} 
                    />
                  </BookImageWrapper>
                  <BookContent>
                    <BookTitle>{book.title}</BookTitle>
                    <BookGenre>{book.genre || 'Fiction'}</BookGenre>
                    <BookDescription>
                      {book.description.length > 150 
                        ? `${book.description.substring(0, 150)}...` 
                        : book.description
                      }
                    </BookDescription>
                    <BookStats>
                      <Stat>
                        <div className="value">{book.publishedDate ? new Date(book.publishedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : 'TBD'}</div>
                        <div className="label">Published</div>
                      </Stat>
                      <Stat>
                        <div className="value">{book.pages || 'TBD'}</div>
                        <div className="label">Pages</div>
                      </Stat>
                      <Stat>
                        <div className="value">⭐ {book.rating || '4.8'}</div>
                        <div className="label">Rating</div>
                      </Stat>
                    </BookStats>
                    <div style={{ marginTop: '1rem' }}>
                      <ReadMoreButton
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const slug = book.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                          window.location.href = `/book/${book.id}/${slug}`;
                        }}
                      >
                        Read More
                      </ReadMoreButton>
                    </div>
                  </BookContent>
                </BookCard>
              )) : (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'white', padding: '4rem' }}>
                  <h3 style={{ marginBottom: '1rem', fontSize: '2rem' }}>No Books Found</h3>
                  <p style={{ fontSize: '1.1rem', opacity: 0.8 }}>
                    {activeFilter === 'All' 
                      ? 'No published books available yet.' 
                      : `No books found in the ${activeFilter} genre.`
                    }
                  </p>
                </div>
              )}
            </AnimatePresence>
          </BooksGrid>
        )}
        
        <AchievementsSection
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <AchievementsTitle>Book Statistics</AchievementsTitle>
          <AchievementsGrid>
            {achievements.map((achievement, index) => (
              <Achievement
                key={index}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                whileHover={{ scale: 1.1 }}
              >
                <div className="icon">{achievement.icon}</div>
                <div className="number">{achievement.number}</div>
                <div className="text">{achievement.text}</div>
              </Achievement>
            ))}
          </AchievementsGrid>
        </AchievementsSection>
      </ContentWrapper>
      
      <AnimatePresence>
        {selectedBook && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedBook(null)}
          >
            <ModalContent
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseButton onClick={() => setSelectedBook(null)}>×</CloseButton>
              <h2 style={{ marginBottom: '1rem', fontSize: '2rem' }}>{selectedBook.title}</h2>
              <p style={{ marginBottom: '1rem', color: '#4facfe' }}>{selectedBook.genre || 'Fiction'}</p>
              <p style={{ lineHeight: '1.6', marginBottom: '2rem' }}>{selectedBook.description}</p>
              {selectedBook.publishedDate && (
                <p style={{ marginBottom: '2rem', color: '#f093fb', fontSize: '0.9rem' }}>
                  📅 Published: {new Date(selectedBook.publishedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                </p>
              )}
              <div style={{ display: 'flex', gap: '2rem', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#f093fb' }}>{selectedBook.publishedDate ? new Date(selectedBook.publishedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : 'TBD'}</div>
                  <div style={{ fontSize: '0.9rem', opacity: '0.8' }}>Published</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#f093fb' }}>{selectedBook.pages || 'TBD'}</div>
                  <div style={{ fontSize: '0.9rem', opacity: '0.8' }}>Pages</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#f093fb' }}>⭐ {selectedBook.rating || '4.8'}</div>
                  <div style={{ fontSize: '0.9rem', opacity: '0.8' }}>Rating</div>
                </div>
              </div>
            </ModalContent>
          </Modal>
        )}
      </AnimatePresence>
    </PortfolioContainer>
  );
};

export default Portfolio;
